import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface BreadcrumbItem {
  label: string;
  href?: string;
  icon?: React.ReactNode;
}

interface BreadcrumbsProps {
  items?: BreadcrumbItem[];
  className?: string;
}

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ items, className = '' }) => {
  const location = useLocation();
  const { t } = useTranslation();

  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const breadcrumbs: BreadcrumbItem[] = [
      { label: t('breadcrumbs.dashboard'), href: '/admin', icon: <Home className="w-4 h-4" /> }
    ];

    const routeKeys: Record<string, string> = {
      posts: 'breadcrumbs.posts',
      contacts: 'breadcrumbs.contacts',
      users: 'breadcrumbs.users',
      settings: 'breadcrumbs.settings',
      newsletter: 'breadcrumbs.newsletter',
      nif: 'breadcrumbs.nif',
      'analise-visto': 'breadcrumbs.visaAnalysis',
      'analise-residencia': 'breadcrumbs.residenceAnalysis',
      advogado: 'breadcrumbs.advogado',
      contrato: 'breadcrumbs.contrato',
      orcamento: 'breadcrumbs.orcamento',
      servicos: 'breadcrumbs.servicos',
      faq: 'breadcrumbs.faq',
      testimonials: 'breadcrumbs.testimonials',
      equipe: 'breadcrumbs.equipe',
      invites: 'breadcrumbs.invites',
      'redes-sociais': 'breadcrumbs.socialMedia',
      crm: 'breadcrumbs.crm',
      clientes: 'breadcrumbs.clientes',
      vendas: 'breadcrumbs.vendas',
      pagamentos: 'breadcrumbs.pagamentos',
      'password-reset': 'breadcrumbs.passwordReset',
      new: 'breadcrumbs.new',
      edit: 'breadcrumbs.edit'
    };

    let currentPath = '/admin';
    const adminSegments = pathSegments[0] === 'admin' ? pathSegments.slice(1) : pathSegments;

    adminSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      const isLast = index === adminSegments.length - 1;
      const labelKey = routeKeys[segment];
      const label = labelKey
        ? t(labelKey)
        : segment.charAt(0).toUpperCase() + segment.slice(1);

      breadcrumbs.push({
        label,
        href: isLast ? undefined : currentPath
      });
    });

    return breadcrumbs;
  };

  const breadcrumbItems = items || generateBreadcrumbs();

  return (
    <nav className={`flex items-center space-x-1 text-sm text-gray-500 ${className}`} aria-label="Breadcrumb">
      {breadcrumbItems.map((item, index) => (
        <React.Fragment key={index}>
          {index > 0 && <ChevronRight className="w-4 h-4 text-gray-400" />}

          {item.href ? (
            <Link
              to={item.href}
              className="flex items-center space-x-1 text-gray-500 hover:text-gray-700 transition-colors"
            >
              {item.icon && <span className="flex items-center">{item.icon}</span>}
              <span>{item.label}</span>
            </Link>
          ) : (
            <span className="flex items-center space-x-1 text-gray-900 font-medium">
              {item.icon && <span className="flex items-center">{item.icon}</span>}
              <span>{item.label}</span>
            </span>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
};

export default Breadcrumbs;
